import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, FileText, Landmark, Loader2, Receipt, X } from 'lucide-react';
import { api, WORKER_API_BASE, iframeClientParam } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { paymentVariance } from '../lib/auditTrailHelpers';
import { isTemporaryAccount } from '../lib/coa-hierarchy';
import { stageOf } from '../lib/lifecycleStepper';
import LifecycleStepper from '../components/LifecycleStepper';
import { Tag, LedgerBlock, TempCancelNote, money, JeLine } from './ledger-shared';
import { useToast } from './Toast';

interface Props {
  entry: any;
  onClose: () => void;
  /** Optional return action when this popup is opened above another popup. */
  onBack?: () => void;
  /** Render without the fixed overlay so a parent popup can host the content. */
  embedded?: boolean;
}

interface LinkedTx {
  id: string; transaction_date: string; description: string; bank_name: string | null;
  statement_id?: string | null; statement_file_name?: string | null;
  amount: number; allocated_amount: number | null; match_status: string;
  payment_voucher_no: string | null;
}

interface InvoiceDetail {
  id: string; invoice_number: string; total: number; currency: string; direction?: string;
  file_id?: string | null; source_page?: number | null;
  linked_transactions?: LinkedTx[];
  journal_entries?: { id: string; entry_number: string; entry_date: string; reference_type: string; reference_id?: string | null; entry_source: string; lines: JeLine[] }[];
}

const hkd = (n: number | null | undefined) => `HKD ${money(n)}`;

/** Where a voucher's letter comes from — the "source" of the booking. */
const SOURCE_LABELS: Record<string, [string, string, string]> = {
  B: ['Bank statement', '銀行月結單', '银行月结单'],
  C: ['Card statement', '信用卡月結單', '信用卡月结单'],
  I: ['Invoice', '發票', '发票'],
  R: ['Expense receipt', '費用收據', '费用收据'],
  P: ['Payment (confirmed match)', '付款（已確認配對）', '付款（已确认配对）'],
  M: ['Manual journal', '手動日誌', '手动日志'],
  D: ['Depreciation', '折舊', '折旧'],
  Y: ['Year-end close', '年結', '年结'],
  T: ['Tax provision', '稅項撥備', '税项拨备'],
};

export function sourceLabel(entryNumber?: string | null): [string, string, string] | null {
  const letter = /^[A-Z]/.exec(entryNumber || '')?.[0];
  return letter ? SOURCE_LABELS[letter] ?? null : null;
}

export default function JournalEntryPopup({ entry, onClose, onBack, embedded = false }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const entryId = entry?.id || entry?.entry_id;

  useEffect(() => {
    if (embedded || !entry) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') (onBack || onClose)();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [embedded, entry, onClose, onBack]);

  const detailQuery = useQuery({
    queryKey: ['journal-entry-popup-lines', entryId],
    queryFn: () => api(`/bookkeeping/entries/${entryId}`),
    enabled: !!entryId,
  });

  const auditQuery = useQuery({
    queryKey: ['journal-entry-popup-audit', entryId],
    queryFn: () => api(`/bookkeeping/entries/${entryId}/audit-trail`),
    enabled: !!entryId,
  });

  const detail: any = detailQuery.data || {};
  const auditTrail = Array.isArray(auditQuery.data) ? auditQuery.data : [];
  const links = detail.resolved_links || entry?.resolved_links || {};
  const refType: string | undefined = detail.reference_type || entry?.reference_type;

  // ── Invoice context: for a payment voucher the linked invoice(s); for an
  // invoice voucher, itself. This is what lets the popup show BOTH sides.
  const invoiceIds: string[] = useMemo(() => {
    if (!refType || !entry?.reference_id) return [] as string[];
    if (['invoice', 'receipt', 'expense_receipt'].includes(refType)) return [entry.reference_id];
    if (refType === 'payment') return (links.linked_invoices || []).map((i: any) => i.id);
    return [];
  }, [refType, entry?.reference_id, links.linked_invoices]);

  const invoiceQuery = useQuery({
    queryKey: ['je-popup-invoices', invoiceIds.join(',')],
    queryFn: async () => {
      const results = await Promise.allSettled(invoiceIds.map(id => api(`/invoices/${id}`)));
      return results.filter(r => r.status === 'fulfilled').map(r => (r as PromiseFulfilledResult<any>).value as InvoiceDetail);
    },
    enabled: invoiceIds.length > 0,
  });

  if (!entry) return null;

  const rawLines: any[] = detail.lines || entry.lines || [];
  const files: any[] = detail.files || entry.files || [];
  const thisLines: JeLine[] = rawLines.map(l => ({
    account_code: l.account_code, account_name: l.account_name,
    debit: l.debit || 0, credit: l.credit || 0,
  }));
  const totalDebit = entry.total_debit ?? thisLines.reduce((s, l) => s + (l.debit || 0), 0);
  const totalCredit = entry.total_credit ?? thisLines.reduce((s, l) => s + (l.credit || 0), 0);
  const balanced = Math.abs(totalDebit - totalCredit) < 0.005;
  const src = sourceLabel(entry.entry_number);
  const invRows: InvoiceDetail[] = invoiceQuery.data || [];

  // The bank line behind this voucher (or behind the linked invoice).
  const txFromInvoice: LinkedTx | undefined = invRows
    .flatMap(r => r.linked_transactions || [])
    .find(t => (refType === 'payment' ? t.id === entry.reference_id : t.match_status !== 'unmatched'));
  const txInfo = {
    description: txFromInvoice?.description || links.bank_transaction?.description,
    date: txFromInvoice?.transaction_date || entry.entry_date,
    statement_label: links.bank_statement?.statement_label || txFromInvoice?.bank_name || null,
    statement_file: txFromInvoice?.statement_file_name || links.bank_statement?.file_name || null,
    statement_id: txFromInvoice?.statement_id || links.bank_transaction?.statement_id || null,
    amount: txFromInvoice ? (txFromInvoice.allocated_amount ?? txFromInvoice.amount) : links.bank_transaction?.amount,
    match_status: txFromInvoice?.match_status || links.bank_transaction?.match_status,
  };
  const showBankCard = !!txInfo.description && ['payment', 'bank_transaction', 'card_statement'].includes(refType || '');
  const showInvoiceCards = invRows.length > 0 || !!links.invoice;

  // GL pairing: bank-side blocks and invoice-side blocks.
  const invoiceSideBlocks = refType === 'payment'
    ? invRows.map(inv => ({ inv, je: (inv.journal_entries || []).find(e => e.reference_type === 'invoice') || null }))
    : [{ inv: invRows[0], je: null as any }]; // this voucher IS the invoice side
  const paymentSideBlocks = refType === 'payment'
    ? [{ je: null as any, tx: txFromInvoice }] // this voucher IS the bank side
    : invRows.flatMap(inv => (inv.journal_entries || []).filter(e => e.reference_type === 'payment')
        .map(e => ({ je: e, tx: (inv.linked_transactions || []).find(t => t.id === e.reference_id) })));
  const pairView = (refType === 'payment' && invRows.length > 0) ||
    (['invoice', 'receipt', 'expense_receipt'].includes(refType || '') && paymentSideBlocks.length > 0);

  const paidTotal = paymentSideBlocks.reduce((s, pb) => s + (pb.tx ? (pb.tx.allocated_amount ?? pb.tx.amount) : 0), 0)
    || txInfo.amount || 0;
  const variance = invRows.length ? paymentVariance(paidTotal || txInfo.amount || 0, invRows as any) : null;

  const openStatementPdf = async (statementId: string) => {
    try {
      const token = localStorage.getItem('token') || '';
      const resp = await fetch(`${WORKER_API_BASE}/bank-statements/${statementId}/file?token=${token}${iframeClientParam()}`);
      if (!resp.ok) { toast.error(tr('Could not open PDF', '無法開啟 PDF', '无法开启 PDF')); return; }
      const url = URL.createObjectURL(await resp.blob());
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error(tr('Could not open PDF', '無法開啟 PDF', '无法开启 PDF')); }
  };

  let createdBy: any = entry.created_by;
  if (typeof createdBy === 'string') { try { createdBy = JSON.parse(createdBy); } catch { createdBy = null; } }

  const content = (
    <>
      {!embedded && (
        <div className="flex items-start justify-between gap-4 border-b bg-muted/20 p-5">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {tr('Voucher', '記帳憑證', '记帐凭证')}
              {src ? ` · ${tr(src[0], src[1], src[2])}` : ''}
            </p>
            <h3 id="journal-entry-popup-title" className="mt-1 text-xl font-bold text-foreground font-mono">
              {entry.entry_number || entry.id}
            </h3>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span className="font-mono">{entry.entry_date}</span>
              <LifecycleStepper
                type="voucher"
                variant="full"
                stage={stageOf('voucher', { status: entry.status || 'booked' })}
                stageSince={entry.updated_at}
              />
              <Tag kind={entry.entry_source === 'manual' ? 'manual' : 'auto'} />
              <span className="font-mono">{hkd(totalDebit)}</span>
              <span className={balanced ? 'text-green-700 dark:text-green-300 font-medium' : 'text-red-700 dark:text-red-300 font-medium'}>
                {balanced ? '✓ Dr = Cr' : `⚠ ${tr('unbalanced', '不平衡', '不平衡')} ${money(totalDebit - totalCredit)}`}
              </span>
              <span>{createdBy?.name || createdBy?.email || tr('System', '系統', '系统')}</span>
            </div>
            {entry.description && <p className="mt-2 text-sm text-foreground">{entry.description}</p>}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1 rounded-lg border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={tr('Back to account transactions', '返回科目交易', '返回科目交易')}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {tr('Back', '返回', '返回')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border bg-background p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={tr('Close', '關閉', '关闭')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 space-y-4 overflow-y-auto p-5" data-testid="je-popup-body">

          {/* ── 1. Documents: the paper behind the voucher ── */}
          {(showBankCard || showInvoiceCards) && (
            <div className="border rounded-lg p-3">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2.5">{tr('Documents', '文件', '文件')}</p>
              <div className="relative pl-5 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-0.5 before:bg-border">
                {showBankCard && (
                  <div className="relative bg-muted/40 border rounded-lg px-3 py-2 mb-2 flex items-center gap-2.5">
                    <span className="absolute -left-[18px] top-3 h-2.5 w-2.5 rounded-full bg-card border-2 border-blue-400" />
                    <Landmark className="h-4 w-4 text-blue-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold truncate">{txInfo.description}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        <span className="font-mono">{txInfo.date}</span>
                        {txInfo.statement_label ? ` · ${txInfo.statement_label}` : ''}
                        {txInfo.statement_file ? <> · {tr('from', '來自', '来自')} <b>{txInfo.statement_file}</b></> : null}
                      </p>
                    </div>
                    <span className="font-mono text-xs font-semibold whitespace-nowrap">{hkd(txInfo.amount)}</span>
                    {txInfo.statement_id && (
                      <button onClick={() => openStatementPdf(txInfo.statement_id!)}
                        className="text-[11px] px-2 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:underline shrink-0 dark:bg-blue-900/30 dark:border-blue-800">
                        {tr('View PDF', '查看 PDF', '查看 PDF')}
                      </button>
                    )}
                  </div>
                )}
                {(invRows.length > 0 ? invRows : links.invoice ? [links.invoice] : []).map((inv: any) => (
                  <div key={inv.id} className="relative bg-muted/40 border rounded-lg px-3 py-2 flex items-center gap-2.5">
                    <span className="absolute -left-[18px] top-3 h-2.5 w-2.5 rounded-full bg-card border-2 border-green-500" />
                    <Receipt className="h-4 w-4 text-green-900 dark:text-green-200 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold truncate">{inv.invoice_number}</p>
                      <p className="text-[11px] text-muted-foreground truncate">
                        {inv.direction === 'incoming' ? tr('Bill / purchase', '帳單／採購', '账单／采购') : tr('Sales invoice', '銷售發票', '销售发票')}
                        {inv.vendor_or_customer ? ` · ${inv.vendor_or_customer}` : ''}
                      </p>
                    </div>
                    <span className="font-mono text-xs font-semibold whitespace-nowrap">{hkd(inv.total)}</span>
                    {inv.file_id && (
                      <a href={`${WORKER_API_BASE}/file-storage/${inv.file_id}/download?inline=1&token=${localStorage.getItem('token') || ''}${iframeClientParam()}${inv.source_page ? `#page=${inv.source_page}` : ''}`}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] px-2 py-1 rounded-md border border-green-200 bg-green-50 text-green-900 dark:text-green-200 dark:bg-green-900/30 dark:border-green-800 hover:underline shrink-0">
                        {tr('View PDF', '查看 PDF', '查看 PDF')}{inv.source_page ? ` (p.${inv.source_page})` : ''}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── 2. GL postings ── */}
          <div className="border rounded-lg p-3">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-2.5">
              {tr('GL postings — where money is debited & credited', '過賬分錄——借貸科目', '过账分录——借贷科目')}
            </p>

            {pairView ? (
              <>
                <div className="grid md:grid-cols-2 gap-3">
                  {/* bank side */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-800 text-xs font-bold dark:bg-blue-950/40 dark:text-blue-300">
                      <Landmark className="h-3.5 w-3.5" /> {tr('Bank statement side', '銀行月結單方', '银行月结单方')}
                    </div>
                    {refType === 'payment' ? (
                      <div>
                        <div className="flex items-center gap-2 px-3 pt-2 text-[11px]">
                          <span className="font-mono font-semibold">{entry.entry_number}</span>
                          <span className="text-muted-foreground font-mono">{entry.entry_date}</span>
                          <span className="ml-auto"><Tag kind={entry.entry_source === 'manual' ? 'manual' : 'auto'} /></span>
                        </div>
                        <LedgerBlock lines={thisLines} footer={txInfo.date && txInfo.amount ? `${txInfo.date} · ${money(txInfo.amount)}` : undefined} />
                      </div>
                    ) : paymentSideBlocks.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-3">{tr('No payment entry yet.', '尚未有付款分錄。', '尚未有付款分录。')}</p>
                    ) : paymentSideBlocks.map(({ je, tx }, idx) => (
                      <div key={je?.id || idx}>
                        <div className="flex items-center gap-2 px-3 pt-2 text-[11px]">
                          <span className="font-mono font-semibold">{je.entry_number}</span>
                          <span className="text-muted-foreground font-mono">{je.entry_date}</span>
                          <span className="ml-auto"><Tag kind={je.entry_source === 'manual' ? 'manual' : 'auto'} /></span>
                        </div>
                        <LedgerBlock lines={je.lines} footer={tx ? `${tx.transaction_date} · ${money(tx.allocated_amount ?? tx.amount)}` : undefined} />
                      </div>
                    ))}
                  </div>

                  {/* invoice side */}
                  <div className="border rounded-lg overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 text-purple-800 text-xs font-bold dark:bg-purple-950/40 dark:text-purple-300">
                      <Receipt className="h-3.5 w-3.5" /> {tr('Invoice side', '發票方', '发票方')}{invoiceSideBlocks.length > 1 ? ` — ${invoiceSideBlocks.length} ${tr('entries', '分錄', '分录')}` : ''}
                    </div>
                    {invoiceSideBlocks.map(({ inv, je }, idx) => (
                      <div key={inv?.id || idx} className="border-b last:border-b-0">
                        <div className="flex items-center gap-2 px-3 pt-2 text-[11px]">
                          <span className="font-bold text-xs">{inv?.invoice_number || links.invoice?.invoice_number}</span>
                          <span className="font-mono font-semibold">{refType === 'payment' ? je?.entry_number : entry.entry_number}</span>
                          {refType === 'payment' && je && <span className="ml-auto"><Tag kind={je.entry_source === 'manual' ? 'manual' : 'auto'} /></span>}
                        </div>
                        {refType === 'payment'
                          ? (je ? <LedgerBlock lines={je.lines} /> : <p className="text-xs text-muted-foreground px-3 pb-2">{tr('Not yet posted to GL', '尚未過賬至總賬', '尚未过账至总账')}</p>)
                          : <LedgerBlock lines={thisLines} />}
                      </div>
                    ))}
                  </div>
                </div>

                {variance != null && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200">
                    ⚠ <b>{tr('Variance', '差異', '差异')} {variance > 0 ? '+' : ''}{money(variance)}</b>
                    {' — '}{tr('payment', '付款', '付款')} {money(paidTotal || txInfo.amount || 0)} vs {tr('invoice total', '發票總額', '发票总额')} {money(invRows.reduce((s, r) => s + r.total, 0))}
                  </div>
                )}

                <TempCancelNote
                  contraLines={refType === 'payment' ? thisLines : paymentSideBlocks.flatMap(pb => pb.je?.lines || [])}
                  otherLines={refType === 'payment'
                    ? invoiceSideBlocks.flatMap(b => b.je?.lines || [])
                    : thisLines}
                />
              </>
            ) : (
              <>
                <LedgerBlock lines={thisLines} />
                {['bank_transaction', 'card_statement'].includes(refType || '') && thisLines.some(l => isTemporaryAccount(l.account_name)) && (
                  <p className="mt-2 text-[11.5px] text-muted-foreground">
                    {tr('Awaiting match — the money is parked in a temporary account until you link it to an invoice or classify it.', '待配對——資金暫存於暫記科目，直至連結發票或重新分類。', '待配对——资金暂存于暂记科目，直至连结发票或重新分类。')}
                  </p>
                )}
              </>
            )}
          </div>

          {/* ── 3. Attached files ── */}
          {files.length > 0 && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-foreground">{tr('Attached files', '附加文件', '附加文件')}</h4>
              <div className="flex flex-wrap gap-2">
                {files.map(file => (
                  <span key={file.id} className="inline-flex items-center gap-1 rounded-full border bg-background px-3 py-1 text-xs text-foreground">
                    <FileText className="h-3 w-3" />
                    {file.filename}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── 4. Related records ── */}
          {(links.bank_statement || links.bank_transaction || links.invoice || links.linked_invoices?.length || links.reversal) && (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-foreground">{tr('Related records', '相關記錄', '相关记录')}</h4>
              <div className="flex flex-wrap gap-2">
                {links.bank_statement && (
                  <button type="button"
                    onClick={() => navigate('/bank-statements', { state: { highlight: links.bank_statement.id, highlightTx: links.bank_transaction?.id } })}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs text-primary hover:bg-muted">
                    <ExternalLink className="h-3 w-3" />
                    {tr('Statement', '結單', '结单')}: {links.bank_statement.statement_label || links.bank_statement.file_name || links.bank_statement.id}
                  </button>
                )}
                {links.bank_transaction && links.bank_transaction.statement_id && (
                  <button type="button"
                    onClick={() => navigate(`/bank-statements/review/${links.bank_transaction.statement_id}`, { state: { highlight: links.bank_transaction.id } })}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs text-primary hover:bg-muted">
                    <ExternalLink className="h-3 w-3" />
                    {tr('Transaction', '交易', '交易')}: {links.bank_transaction.description || links.bank_transaction.id}
                  </button>
                )}
                {links.invoice && (
                  <button type="button"
                    onClick={() => navigate(links.invoice.direction === 'incoming' ? '/ap' : '/ar', { state: { highlight: links.invoice.id } })}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs text-primary hover:bg-muted">
                    <ExternalLink className="h-3 w-3" />
                    {links.invoice.direction === 'incoming' ? tr('Bill', '帳單', '账单') : tr('Invoice', '發票', '发票')}: {links.invoice.invoice_number}
                  </button>
                )}
                {(links.linked_invoices || []).map((invoice: any) => (
                  <button key={invoice.id} type="button"
                    onClick={() => navigate('/ap', { state: { highlight: invoice.id } })}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs text-primary hover:bg-muted">
                    <ExternalLink className="h-3 w-3" />
                    {tr('Bill', '帳單', '账单')}: {invoice.invoice_number}
                  </button>
                ))}
                {links.reversal && (
                  <button type="button"
                    onClick={() => navigate('/GJE', { state: { highlight: links.reversal.id } })}
                    className="inline-flex items-center gap-1.5 rounded-full border bg-background px-3 py-1 text-xs text-primary hover:bg-muted">
                    <ExternalLink className="h-3 w-3" />
                    {tr('Reversal', '沖銷', '冲销')}: {links.reversal.entry_number}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── 5. History ── */}
          <div>
            <h4 className="mb-2 text-sm font-semibold text-foreground">{tr('Audit trail', '審計軌跡', '审计轨迹')}</h4>
            {auditQuery.isLoading ? (
              <div className="flex items-center justify-center gap-2 rounded-xl border bg-background p-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {tr('Loading audit trail…', '載入審計軌跡…', '载入审计轨迹…')}
              </div>
            ) : auditTrail.length === 0 ? (
              <div className="rounded-xl border border-dashed bg-background p-5 text-sm text-muted-foreground">
                {tr('No audit history is available for this entry.', '此分錄沒有審計歷史。', '此分录没有审计历史。')}
              </div>
            ) : (
              <div className="space-y-2 rounded-xl border bg-background p-4">
                {auditTrail.map((trail: any) => (
                  <div key={trail.id} className="border-l-2 border-muted pl-3 text-xs">
                    <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
                      <span className="font-mono">{new Date(trail.created_at).toLocaleString()}</span>
                      <span className="font-medium text-foreground">{trail.user_email}</span>
                      <span className="rounded bg-muted px-1.5 py-0.5">{trail.action}</span>
                    </div>
                    {(trail.changes || []).filter((change: any) => !change.field.startsWith('_')).map((change: any, index: number) => (
                      <p key={index} className="mt-1 text-muted-foreground">
                        <span className="font-medium">{change.field}</span>: {JSON.stringify(change.old)} → {JSON.stringify(change.new)}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {detailQuery.isLoading && (
            <p className="text-xs text-muted-foreground">{tr('Loading…', '載入中…', '载入中…')}</p>
          )}
      </div>
    </>
  );

  if (embedded) {
    return <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{content}</div>;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="journal-entry-popup-title"
        className="flex h-[85vh] w-[80vw] max-w-none flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl"
        onClick={event => event.stopPropagation()}
      >
        {content}
      </div>
    </div>
  );
}
